import type { DateTime } from "luxon"
import { datetime, RRule } from "rrule"
import type { Options } from "rrule"

import type { AppLocalization } from "../../../../../../modules/localization/appLocalization"
import type { TranslationKey } from "../../../../../../modules/localization/translations"
import type { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"

import type { RepeatOrderFormValues } from "./RepeatOrderDialog"

// index = rrule weekday number
const WEEK_DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
const WORKING_DAYS = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR]
const MAX_REPEAT_SPAN_DAYS = 366
const MAX_REPEAT_DAYS = 500
export const MAX_REPEAT_INTERVAL = 30
export const MAX_REPEAT_COUNT = 366

type RepeatValues = Partial<RepeatOrderFormValues>

export type RepeatDeliveryDay = Readonly<{
    date: DateTime
    isDeselected: boolean
    isStart: boolean
}>

export const prepareInitialValues = (
    details: Partial<RepeatOrderFormValues> | null | undefined,
    startDate: DateTime | null,
): RepeatOrderFormValues => {
    return {
        repeatType: details?.repeatType ?? "day",
        repeatInterval: details?.repeatInterval ?? 1,
        dayMode: details?.dayMode ?? "weekdays",
        startDate,
        endType: details?.endType ?? "on",
        endDate: details?.endDate ?? null,
        repeatCount: details?.repeatCount ?? 2,
        weekDays: details?.weekDays ?? [],
        deselectedDates: details?.deselectedDates ?? [],
    }
}

// the number fields clamp on blur only, and Enter submits without one
export function clampRepeatLimits(values: RepeatOrderFormValues): RepeatOrderFormValues {
    return {
        ...values,
        repeatInterval: Math.min(values.repeatInterval, MAX_REPEAT_INTERVAL),
        repeatCount: Math.min(values.repeatCount, MAX_REPEAT_COUNT),
    }
}

export function toRepeatDateKey(date: DateTime): string {
    return date.toFormat("yyyy-MM-dd")
}

export function weekDayNamesFromRRule(byweekday: ReadonlyArray<number>): string[] {
    return byweekday.map((weekday) => WEEK_DAY_NAMES[weekday])
}

function toRRuleDate(date: DateTime) {
    return datetime(date.year, date.month, date.day)
}

function toRRuleOptions(values: RepeatValues, startDate: DateTime): Partial<Options> {
    const isWeekly = values.repeatType === "week"

    return {
        freq: isWeekly ? RRule.WEEKLY : RRule.DAILY,
        interval: values.repeatInterval || 1,
        dtstart: toRRuleDate(startDate),
        ...(isWeekly && { byweekday: (values.weekDays ?? []).map((day) => WEEK_DAY_NAMES.indexOf(day)) }),
        ...(!isWeekly && values.dayMode === "weekdays" && { byweekday: WORKING_DAYS }),
    }
}

// while typing, the field gives an invalid date (25/11/YYYY), then the years 0002, 0020, 0202
function isFullyTypedDate(date: DateTime): boolean {
    return date.isValid && date.year >= 1000
}

export function getRepeatRangeErrorId({
    startDate,
    endType,
    endDate,
    repeatCount,
}: RepeatValues): TranslationKey | null {
    if (!startDate) return "create-order-drawer-repeat-order-start-date-missing-error"
    if (endType === "after" && !repeatCount) return "create-order-drawer-repeat-order-count-missing-error"
    if (endType !== "on") return null
    if (!endDate || !isFullyTypedDate(endDate)) return "create-order-drawer-repeat-order-end-date-missing-error"

    const start = startDate.startOf("day")
    const end = endDate.startOf("day")
    // negated: an invalid start date must fail too
    if (!(end >= start)) return "create-order-drawer-repeat-order-end-before-start-error"

    return end.diff(start, "days").days > MAX_REPEAT_SPAN_DAYS ? "create-order-drawer-repeat-order-span-error" : null
}

export function getRepeatDeliveryDays(values: RepeatValues): ReadonlyArray<RepeatDeliveryDay> {
    const { startDate, endType, endDate, repeatType, weekDays, repeatCount } = values

    if (!startDate || getRepeatRangeErrorId(values)) return []
    if (repeatType === "week" && !weekDays?.length) return []
    // an open-ended pattern from a saved order has no list of days
    if (endType !== "on" && endType !== "after") return []

    const deselectedDates = new Set(values.deselectedDates)
    const days: RepeatDeliveryDay[] = []
    let deliveryCount = 0

    new RRule({
        ...toRRuleOptions(values, startDate),
        ...(endType === "on" && endDate && { until: toRRuleDate(endDate) }),
    }).all((occurrence) => {
        if (days.length >= MAX_REPEAT_DAYS) return false
        // "after N" counts real deliveries, so a deselected day extends the list
        if (endType === "after" && deliveryCount >= (repeatCount ?? 0)) return false

        const date = startDate
            .set({
                year: occurrence.getUTCFullYear(),
                month: occurrence.getUTCMonth() + 1,
                day: occurrence.getUTCDate(),
            })
            .startOf("day")
        const isStart = date.hasSame(startDate, "day")
        const isDeselected = !isStart && deselectedDates.has(toRepeatDateKey(date))

        if (!isDeselected) deliveryCount += 1
        days.push({ date, isDeselected, isStart })

        return true
    })

    return days
}

export function getRecurrenceErrorId(values: RepeatValues): TranslationKey | null {
    return (
        getRepeatRangeErrorId(values) ??
        (getRepeatDeliveryDays(values).some((day) => !day.isDeselected)
            ? null
            : "create-order-drawer-repeat-order-no-deliveries-error")
    )
}

export function getRepeatExceptions(values: RepeatValues): string[] {
    return getRepeatDeliveryDays(values)
        .filter((day) => day.isDeselected)
        .map((day) => toRepeatDateKey(day.date))
}

export function makeRecurrencyICalFormat(recurrency: RepeatOrderFormValues): string {
    const { startDate, endType, endDate } = recurrency

    if (!startDate || (endType === "on" && !endDate)) return ""

    const lastDelivery = getRepeatDeliveryDays(recurrency)
        .filter((day) => !day.isDeselected)
        .at(-1)
    // UNTIL for "after N" too: COUNT would depend on how the backend counts exceptions
    const until = endType === "on" ? endDate : lastDelivery?.date

    return new RRule({
        ...toRRuleOptions(recurrency, startDate),
        ...(until && { until: toRRuleDate(until) }),
        // No `tzid`: only Manager-native recurrences reach this (an Outlook pattern is forwarded
        // verbatim), and the backend parser does not accept `DTSTART;TZID=` yet.
    }).toString()
}

export function makeRecurrencePayload(recurrency: RepeatOrderFormValues) {
    const exceptions = getRepeatExceptions(recurrency)

    return {
        pattern: makeRecurrencyICalFormat(recurrency),
        ...(exceptions.length > 0 && { exceptions }),
    }
}

export function groupRepeatDaysByMonth(
    days: ReadonlyArray<RepeatDeliveryDay>,
): ReadonlyArray<readonly [string, ReadonlyArray<RepeatDeliveryDay>]> {
    const months = new Map<string, RepeatDeliveryDay[]>()

    for (const day of days) {
        const month = day.date.toFormat("MMMM yyyy")
        months.set(month, [...(months.get(month) ?? []), day])
    }

    return [...months]
}

export function formatRepeatDaysRange(
    days: ReadonlyArray<RepeatDeliveryDay>,
    dateTimeFactory: DateTimeFactory,
): string {
    const deliveries = days.filter((day) => !day.isDeselected)
    const first = deliveries.at(0)
    const last = deliveries.at(-1)

    if (!first || !last) return ""

    const from = dateTimeFactory.formatShortWeekdayDate(first.date)

    return first === last ? from : `${from} – ${dateTimeFactory.formatShortWeekdayDate(last.date)}`
}

type L10n = Pick<AppLocalization, "getString">

function formatRecurrenceInterval(values: RepeatValues, l10n: L10n): string {
    const repeatType = values.repeatType === "week" ? "week" : "day"
    const repeatIntervalCount = values.repeatInterval ?? 1

    return repeatType === "week"
        ? repeatIntervalCount > 1
            ? l10n.getString("create-order-drawer-repeat-order-summary-interval-weeks", {
                  count: repeatIntervalCount,
              })
            : l10n.getString("create-order-drawer-repeat-order-summary-interval-week")
        : repeatIntervalCount > 1
          ? l10n.getString("create-order-drawer-repeat-order-summary-interval-days", {
                count: repeatIntervalCount,
            })
          : l10n.getString("create-order-drawer-repeat-order-summary-interval-day")
}

// `weekDays` holds stable English names; shown in week order, in the date's locale
function formatWeekDayNames(weekDays: ReadonlyArray<string>, startDate: DateTime | null | undefined): string {
    return WEEK_DAY_NAMES.flatMap((name, index) =>
        weekDays.includes(name) ? [startDate ? startDate.set({ weekday: index + 1 }).toFormat("cccc") : name] : [],
    ).join(", ")
}

// the space is added here: Fluent trims a message's leading whitespace
function formatRecurrenceDays(values: RepeatValues, l10n: L10n): string {
    if (values.repeatType === "day" && values.dayMode === "weekdays") {
        return ` ${l10n.getString("create-order-drawer-repeat-order-summary-weekdays-suffix")}`
    }

    return values.weekDays && values.weekDays.length
        ? ` ${l10n.getString("create-order-drawer-repeat-order-summary-days-suffix", {
              days: formatWeekDayNames(values.weekDays, values.startDate),
          })}`
        : ""
}

function formatRecurrenceExceptions(values: RepeatValues, l10n: L10n): string {
    const count = getRepeatExceptions(values).length

    return count ? ` ${l10n.getString("create-order-drawer-repeat-order-summary-exceptions-suffix", { count })}` : ""
}

export function formatRecurrence({
    values,
    l10n,
}: Readonly<{
    values: RepeatValues
    l10n: L10n
}>): string {
    const startDate = values.startDate
        ? values.startDate.toLocaleString({ month: "long", day: "numeric", year: "numeric" })
        : ""

    const repeatCount = values.repeatCount
        ? l10n.getString("create-order-drawer-repeat-order-summary-repeat-count-suffix", { count: values.repeatCount })
        : ""

    const endDate = values.endDate
        ? l10n.getString("create-order-drawer-repeat-order-summary-end-date-suffix", {
              date: values.endDate.toLocaleString({ month: "long", day: "numeric", year: "numeric" }),
          })
        : ""

    return l10n.getString("create-order-drawer-repeat-order-summary", {
        interval: formatRecurrenceInterval(values, l10n),
        days: formatRecurrenceDays(values, l10n),
        startDate,
        repeatCount,
        endDate,
        exceptions: formatRecurrenceExceptions(values, l10n),
    })
}

export function formatRepeatRule(values: RepeatValues, l10n: L10n): string {
    if (values.repeatType === "day" && (values.repeatInterval ?? 1) === 1) {
        return values.dayMode === "weekdays"
            ? l10n.getString("confirm-order-dialog-repeat-order-summary-weekdays")
            : l10n.getString("confirm-order-dialog-repeat-order-summary-every-day")
    }

    return l10n.getString("confirm-order-dialog-repeat-order-summary-rule", {
        interval: formatRecurrenceInterval(values, l10n),
        days: formatRecurrenceDays(values, l10n),
    })
}

export function formatRepeatSummary({
    values,
    days,
    l10n,
    dateTimeFactory,
}: Readonly<{
    values: RepeatValues
    days: ReadonlyArray<RepeatDeliveryDay>
    l10n: L10n
    dateTimeFactory: DateTimeFactory
}>): string {
    const deselectedCount = days.filter((day) => day.isDeselected).length
    const deliveries = l10n.getString("confirm-order-dialog-repeat-order-summary-deliveries", {
        count: days.length - deselectedCount,
    })
    const deselected = deselectedCount
        ? ` ${l10n.getString("confirm-order-dialog-repeat-order-summary-deselected", { count: deselectedCount })}`
        : ""

    return [formatRepeatRule(values, l10n), formatRepeatDaysRange(days, dateTimeFactory), deliveries + deselected].join(
        " · ",
    )
}
